<script setup lang="ts">
import dayjs from "dayjs";
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { createTicketWidgetClient } from "@/api/client";
import CheckoutStep from "@/components/CheckoutStep.vue";
import LoadingState from "@/components/LoadingState.vue";
import MobileOrderSummary from "@/components/MobileOrderSummary.vue";
import OrderNotFoundState from "@/components/OrderNotFoundState.vue";
import Sprite from "@/components/Sprite.vue";
import SuccessStep from "@/components/SuccessStep.vue";
import TicketsStep from "@/components/TicketsStep.vue";
import { createDateOptions, paymentMethods } from "@/data/checkout";
import type {
  AppliedPromoCode,
  CheckoutForm,
  CheckoutStep as CheckoutStepName,
  OrderResponse,
  OrderTicket,
  PromoCodeDiscountedTicket,
  PromoCodeResponse,
  PromoCodeTicketRequest,
  SuccessTicket,
  TicketDatePrice,
  TicketOrderLine,
  TicketReturnState,
  TicketType,
  TicketWidgetOptions,
} from "@/types";

const props = defineProps<{
  options: TicketWidgetOptions;
}>();

const calendarPriceMonthsAhead = 1;
const calendarDisplayMonthsAhead = 12;
const ticketReturnSuccessMessage =
  "Билет успешно отменен, запрос на возврат средств принят";
const orderPaymentUrlMissingMessage =
  "Не удалось оформить заказ, обратитесь к администратору сайта";
const dates = ref(createDateOptions(dayjs(), getCalendarDisplayDaysAhead()));
const scrollDates = ref(createDateOptions(dayjs(), getCalendarPriceDaysAhead()));
const tickets = ref<TicketType[]>([]);
const quantities = reactive<Record<string, number>>({});
const step = ref<CheckoutStepName>("tickets");
const selectedDateId = ref(dates.value[0]?.id ?? "");
const selectedPaymentId = ref(paymentMethods[0]?.id ?? "bank_card");
const isLoading = ref(true);
const isTicketsLoading = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref("");
const order = ref<OrderResponse | null>(null);
const appliedPromo = ref<AppliedPromoCode | null>(null);
const queryOrderId = ref("");
const isOrderSuccessReturn = ref(false);
const isOrderNotFound = ref(false);
const checkoutStepRef = ref<{ submit: () => void } | null>(null);
const ticketReturnStates = reactive<Record<string, TicketReturnState>>({});
const form = reactive<CheckoutForm>({
  lastName: "",
  firstName: "",
  phone: "",
  email: "",
  personalAgreement: false,
  marketingAgreement: false,
});
let ticketRequestId = 0;

const moneyFormatter = computed(
  () =>
    new Intl.NumberFormat(props.options.locale ?? "ru-RU", {
      maximumFractionDigits: 0,
    }),
);

const selectedTicketLines = computed(() =>
  tickets.value
    .map((ticket) => {
      const quantity = quantities[ticket.id] ?? 0;

      return {
        ticket,
        quantity,
        amount: ticket.price * quantity,
      };
    })
    .filter((line) => line.quantity > 0),
);

const totalQuantity = computed(() =>
  selectedTicketLines.value.reduce((sum, line) => sum + line.quantity, 0),
);

const totalPrice = computed(() =>
  selectedTicketLines.value.reduce((sum, line) => sum + line.amount, 0),
);

const promoDiscountAmount = computed(() =>
  Math.min(appliedPromo.value?.discountAmount ?? 0, totalPrice.value),
);

const checkoutTotalPrice = computed(() =>
  Math.max(totalPrice.value - promoDiscountAmount.value, 0),
);

const mobileOrderTotalPrice = computed(() =>
  step.value === "checkout" ? checkoutTotalPrice.value : totalPrice.value,
);

const mobileOrderDiscountAmount = computed(() =>
  step.value === "checkout" ? promoDiscountAmount.value : 0,
);

const mobileOrderActionLabel = computed(() =>
  step.value === "checkout" ? "Оплатить" : "Купить билет",
);

const selectedDate = computed(() =>
  dates.value.find((date) => date.id === selectedDateId.value),
);

const isOrderLookupMode = computed(() => Boolean(queryOrderId.value));

const successTitle = computed(() =>
  isOrderLookupMode.value && !isOrderSuccessReturn.value
    ? "Ваш заказ"
    : "Ваш заказ успешно оформлен!",
);

const successEmail = computed(() => order.value?.email ?? form.email);

const displayedOrderNumber = computed(
  () =>
    order.value?.tickets.find((ticket) => ticket.orderNumber)?.orderNumber ||
    order.value?.id ||
    queryOrderId.value,
);

const successTickets = computed<SuccessTicket[]>(() => {
  if (order.value?.tickets.length) {
    return order.value.tickets.map((ticket) => {
      const sourceTicket = findSelectedTicketByOrderTicket(ticket);

      return {
        id: ticket.id,
        title: getSuccessTicketTitle(
          ticket.title ?? sourceTicket?.title ?? "Билет",
        ),
        subtitle: ticket.subtitle ?? "Входной билет в парк Дримвуд",
        date: formatApiDate(ticket.validFrom),
        price: ticket.price,
        number: ticket.barcode,
        status: ticket.status,
        isConfirmed: ticket.isConfirmed,
        barcodeImage: ticket.barcodeImage,
        downloadUrl: ticket.downloadUrl,
        returnStatus: getTicketReturnState(ticket.id).status,
        returnMessage: getTicketReturnState(ticket.id).message,
      };
    });
  }

  return selectedTicketLines.value.flatMap((line) =>
    Array.from({ length: line.quantity }, (_, index) => ({
      id: `${line.ticket.id}-${index + 1}`,
      title: getSuccessTicketTitle(line.ticket.title),
      subtitle: "Входной билет в парк Дримвуд",
      date: selectedDate.value?.displayDate ?? selectedDateId.value,
      price: line.ticket.price,
      number: "",
    })),
  );
});

const successTotalQuantity = computed(() =>
  order.value?.tickets.length
    ? order.value.tickets.reduce((sum, ticket) => sum + ticket.quantity, 0)
    : totalQuantity.value,
);

const successTotalPrice = computed(() =>
  order.value?.tickets.length
    ? (order.value.amount ??
      order.value.tickets.reduce((sum, ticket) => sum + ticket.price, 0))
    : checkoutTotalPrice.value,
);

watch(
  () => props.options,
  () => {
    void loadWidget();
  },
  { deep: true },
);

watch(step, () => {
  void scrollToStepTop();
});

watch(selectedDateId, (dateId, previousDateId) => {
  if (!dateId || dateId === previousDateId || isLoading.value) {
    return;
  }

  void loadTicketsByDate(dateId);
});

onMounted(() => {
  void loadWidget();
});

async function scrollToStepTop() {
  await nextTick();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function getSuccessTicketTitle(title: string) {
  return title.replace("Входной билет. ", "").toUpperCase();
}

function formatApiDate(date: string) {
  return dayjs(date).isValid() ? dayjs(date).format("DD.MM.YYYY") : date;
}

function findSelectedTicketByOrderTicket(orderTicket: OrderTicket) {
  return selectedTicketLines.value.find(
    (line) =>
      line.ticket.ticketTypeNo === orderTicket.ticketTypeNo &&
      line.ticket.personTypeNo === orderTicket.personTypeNo &&
      line.ticket.poolNo === orderTicket.poolNo &&
      (line.ticket.date ?? selectedDateId.value) === orderTicket.validFrom,
  )?.ticket;
}

function getCalendarDateRange() {
  const dateFrom = dayjs().startOf("day");
  const dateTo = dateFrom.add(calendarPriceMonthsAhead, "month");

  return {
    dateFrom: dateFrom.format("YYYY-MM-DD"),
    dateTo: dateTo.format("YYYY-MM-DD"),
  };
}

function getCalendarDisplayDaysAhead() {
  const dateFrom = dayjs().startOf("day");

  return dateFrom.add(calendarDisplayMonthsAhead, "month").diff(dateFrom, "day");
}

function getCalendarPriceDaysAhead() {
  const dateFrom = dayjs().startOf("day");

  return dateFrom.add(calendarPriceMonthsAhead, "month").diff(dateFrom, "day");
}

function createPriceMap(ticketPrices: TicketDatePrice[]) {
  return ticketPrices.reduce<Record<string, number>>((prices, ticketPrice) => {
    prices[ticketPrice.date] = ticketPrice.minPrice;
    return prices;
  }, {});
}

function updateCalendarDates(ticketPrices: TicketDatePrice[] = []) {
  const { dateFrom } = getCalendarDateRange();
  const pricesByDate = createPriceMap(ticketPrices);

  dates.value = createDateOptions(
    dateFrom,
    getCalendarDisplayDaysAhead(),
    pricesByDate,
  );
  scrollDates.value = createDateOptions(
    dateFrom,
    getCalendarPriceDaysAhead(),
    pricesByDate,
  );
  selectedDateId.value = dates.value[0]?.id ?? "";
}

async function loadCalendarPrices() {
  const client = createTicketWidgetClient(props.options);
  const { dateFrom, dateTo } = getCalendarDateRange();
  const ticketPrices = await client.getTicketPrices(dateFrom, dateTo);

  updateCalendarDates(ticketPrices);
}

function resetTickets(ticketTypes: TicketType[]) {
  tickets.value = ticketTypes;
  Object.keys(quantities).forEach((ticketId) => {
    delete quantities[ticketId];
  });
  ticketTypes.forEach((ticket) => {
    quantities[ticket.id] = 0;
  });
}

function resetTicketReturnStates() {
  Object.keys(ticketReturnStates).forEach((ticketId) => {
    delete ticketReturnStates[ticketId];
  });
}

function getTicketReturnState(ticketId: string): TicketReturnState {
  return ticketReturnStates[ticketId] ?? { status: "idle" };
}

function getOrderIdFromQuery() {
  if (typeof window === "undefined") {
    return "";
  }

  const params = new URLSearchParams(window.location.search);
  const queryNames = [
    props.options.orderIdQueryParam,
    "order_id",
    "orderId",
    "order",
    "order_number",
  ].filter(Boolean) as string[];

  for (const queryName of queryNames) {
    const value = params.get(queryName)?.trim();

    if (value) {
      return value;
    }
  }

  return "";
}

function getIsOrderSuccessReturnFromQuery() {
  if (typeof window === "undefined") {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  const successFlagNames = [
    props.options.orderSuccessQueryParam,
    "success",
    "order_success",
    "payment_success",
    "paid",
  ].filter(Boolean) as string[];

  for (const queryName of successFlagNames) {
    if (isPositiveQueryFlag(params, queryName)) {
      return true;
    }
  }

  const status = params.get("status")?.trim().toLowerCase();
  const paymentStatus = params.get("payment_status")?.trim().toLowerCase();

  return ["success", "paid", "completed"].includes(status ?? "")
    || ["success", "paid", "completed"].includes(paymentStatus ?? "");
}

function isPositiveQueryFlag(params: URLSearchParams, queryName: string) {
  if (!params.has(queryName)) {
    return false;
  }

  const value = params.get(queryName)?.trim().toLowerCase() ?? "";

  return !["0", "false", "no", "fail", "failed", "error", "cancel", "cancelled"].includes(
    value,
  );
}

async function loadWidget() {
  isLoading.value = true;
  isTicketsLoading.value = false;
  ticketRequestId += 1;
  errorMessage.value = "";
  queryOrderId.value = getOrderIdFromQuery();
  isOrderSuccessReturn.value = getIsOrderSuccessReturnFromQuery();
  isOrderNotFound.value = false;
  order.value = null;
  step.value = queryOrderId.value ? "success" : "tickets";
  appliedPromo.value = null;
  resetTicketReturnStates();
  updateCalendarDates();
  resetTickets([]);

  const client = createTicketWidgetClient(props.options);

  if (queryOrderId.value) {
    await loadOrderTicketsFromQuery(queryOrderId.value, client);
    return;
  }

  try {
    await loadCalendarPrices();
  } catch {
    updateCalendarDates();
  } finally {
    isLoading.value = false;
  }

  try {
    const ticketTypes = await loadTicketsByDate(selectedDateId.value, client);

    if (!ticketTypes) {
      return;
    }

    props.options.onReady?.();
  } catch (error) {
    reportError(error);
  }
}

async function loadOrderTicketsFromQuery(
  orderId: string,
  client = createTicketWidgetClient(props.options),
) {
  errorMessage.value = "";

  try {
    const result = await client.getOrderTickets(orderId);

    order.value = result;
    isOrderNotFound.value = false;
    form.email = result.email ?? "";
    props.options.onReady?.();
  } catch (error) {
    order.value = null;
    isOrderNotFound.value = true;
    errorMessage.value = "";
    props.options.onError?.(error);
  } finally {
    isLoading.value = false;
  }
}

async function loadTicketsByDate(
  dateId: string,
  client = createTicketWidgetClient(props.options),
) {
  const requestId = ++ticketRequestId;

  isTicketsLoading.value = true;
  errorMessage.value = "";
  order.value = null;
  appliedPromo.value = null;
  resetTickets([]);

  try {
    const ticketTypes = await client.getTicketTypes(dateId);

    if (requestId !== ticketRequestId) {
      return null;
    }

    resetTickets(ticketTypes);
    return ticketTypes;
  } catch (error) {
    if (requestId !== ticketRequestId) {
      return null;
    }

    reportError(error);
    return null;
  } finally {
    if (requestId === ticketRequestId) {
      isTicketsLoading.value = false;
    }
  }
}

function setQuantity(ticket: TicketType, nextQuantity: number) {
  quantities[ticket.id] = Math.max(nextQuantity, 0);
  order.value = null;
  appliedPromo.value = null;
}

function goToCheckout() {
  if (!selectedTicketLines.value.length) {
    return;
  }

  errorMessage.value = "";
  step.value = "checkout";
}

function goBackToTickets() {
  errorMessage.value = "";
  appliedPromo.value = null;
  step.value = "tickets";
}

function setAppliedPromo(promo: AppliedPromoCode | null) {
  appliedPromo.value = promo;
}

function createPromoCodeTickets(): PromoCodeTicketRequest[] {
  return selectedTicketLines.value.map((line) => ({
    ticket_type_no: getTicketApiNumber(
      line.ticket.ticketTypeNo,
      "ticket_type_no",
    ),
    person_type_no: getTicketApiNumber(
      line.ticket.personTypeNo,
      "person_type_no",
    ),
    pool_no: getTicketApiNumber(line.ticket.poolNo, "pool_no"),
    valid_from: line.ticket.date ?? selectedDateId.value,
  }));
}

function isSamePromoTicket(
  discountedTicket: PromoCodeDiscountedTicket,
  line: TicketOrderLine,
) {
  return (
    discountedTicket.ticketTypeNo === line.ticket.ticketTypeNo &&
    discountedTicket.personTypeNo === line.ticket.personTypeNo &&
    discountedTicket.poolNo === line.ticket.poolNo &&
    discountedTicket.validFrom === (line.ticket.date ?? selectedDateId.value)
  );
}

function getPromoCodeDiscountAmount(response: PromoCodeResponse) {
  const discountedTickets = response.discountedTickets.filter(
    (ticket) => ticket.errorNo === undefined || ticket.errorNo === 0,
  );

  const discountedTotalPrice = selectedTicketLines.value.reduce((sum, line) => {
    const discountedTicket = discountedTickets.find((ticket) =>
      isSamePromoTicket(ticket, line),
    );
    const ticketPrice = discountedTicket?.discountedPrice ?? line.ticket.price;

    return sum + ticketPrice * line.quantity;
  }, 0);

  return Math.max(totalPrice.value - discountedTotalPrice, 0);
}

async function validatePromoCode(code: string): Promise<AppliedPromoCode> {
  if (!selectedTicketLines.value.length) {
    throw new Error("Добавьте билет для применения промокода.");
  }

  const client = createTicketWidgetClient(props.options);
  const result = await client.checkPromoCode({
    promo_code: code,
    tickets: createPromoCodeTickets(),
  });
  const discountAmount = getPromoCodeDiscountAmount(result);

  return {
    code: result.code || code,
    discountAmount,
  };
}

function handleMobileOrderAction() {
  if (step.value === "tickets") {
    goToCheckout();
    return;
  }

  if (step.value === "checkout") {
    checkoutStepRef.value?.submit();
  }
}

function normalizePhoneForApi(value: string) {
  const digits = value.replace(/\D/g, "");

  if (digits.startsWith("8")) {
    return `+7${digits.slice(1)}`;
  }

  if (digits.startsWith("7")) {
    return `+${digits}`;
  }

  if (digits.startsWith("9")) {
    return `+7${digits}`;
  }

  return `+${digits}`;
}

function getTicketApiNumber(value: number | undefined, field: string) {
  if (value === undefined) {
    throw new Error(`Не удалось определить ${field} для билета.`);
  }

  return value;
}

function createOrderCart() {
  return selectedTicketLines.value.map((line) => ({
    ticket_type_no: getTicketApiNumber(
      line.ticket.ticketTypeNo,
      "ticket_type_no",
    ),
    person_type_no: getTicketApiNumber(
      line.ticket.personTypeNo,
      "person_type_no",
    ),
    pool_no: getTicketApiNumber(line.ticket.poolNo, "pool_no"),
    date: line.ticket.date ?? selectedDateId.value,
    price: line.ticket.price,
    name: line.ticket.title,
    quantity: line.quantity,
  }));
}

async function submitOrder() {
  if (!selectedTicketLines.value.length || isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = "";

  try {
    const client = createTicketWidgetClient(props.options);
    const result = await client.createOrder({
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      phone: normalizePhoneForApi(form.phone),
      email: form.email.trim(),
      payment_method: selectedPaymentId.value,
      promo_code: appliedPromo.value?.code,
      cart: createOrderCart(),
    });

    const paymentUrl = result.paymentUrl?.trim();

    if (!paymentUrl) {
      throw new Error(orderPaymentUrlMissingMessage);
    }

    order.value = result;
    resetTicketReturnStates();
    props.options.onOrderCreated?.(result);
    window.location.assign(paymentUrl);
  } catch (error) {
    reportError(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function returnTicket(ticketId: string) {
  const currentState = getTicketReturnState(ticketId);

  if (currentState.status === "loading" || currentState.status === "success") {
    return;
  }

  ticketReturnStates[ticketId] = { status: "loading" };

  try {
    const client = createTicketWidgetClient(props.options);
    await client.cancelTicket(ticketId);

    ticketReturnStates[ticketId] = {
      status: "success",
      message: ticketReturnSuccessMessage,
    };
  } catch (error) {
    ticketReturnStates[ticketId] = {
      status: "error",
      message: "Попробуйте повторить возврат позже.",
    };
  }
}

function formatPrice(value: number) {
  return `${moneyFormatter.value.format(value)} ₽`;
}

function reportError(error: unknown) {
  errorMessage.value =
    error instanceof Error ? error.message : "Не удалось выполнить запрос.";
  props.options.onError?.(error);
}
</script>

<template>
  <Sprite />

  <main class="w-full bg-transparent">
    <div
      class="mx-auto w-full max-w-[calc(1540px+var(--ticket-widget-side-padding)*2)] px-[var(--ticket-widget-side-padding)]"
      :class="
        step !== 'success' && selectedTicketLines.length
          ? 'pb-[190px] xl:pb-0'
          : ''
      "
    >
      <LoadingState v-if="isLoading" />

      <template v-else>
        <h1
          v-if="step === 'tickets'"
          class="mb-3 lg:mb-6 text-xl lg:text-[28px] font-semibold uppercase leading-6 lg:leading-[32px] tracking-normal text-brand"
        >
          Выберите дату посещения
        </h1>

        <button
          v-if="step === 'checkout'"
          class="flex items-center gap-2 lg:gap-3 mb-3 lg:mb-6 text-xl lg:text-[28px] font-semibold uppercase leading-6 lg:leading-[32px] tracking-normal text-brand cursor-pointer"
          type="button"
          @click="goBackToTickets"
        >
          <svg class="min-w-6 h-6 w-6 text-brand" aria-hidden="true">
            <use href="#ticket-widget-icon-arrow-left" />
          </svg>
          Оформление заказа
        </button>

        <h1
          v-if="step === 'success'"
          class="mb-3 lg:mb-6 text-xl lg:text-[28px] font-semibold uppercase leading-6 lg:leading-[32px] tracking-normal text-brand"
        >
          {{ successTitle }}
        </h1>

        <div
          v-if="errorMessage && !isOrderNotFound"
          class="mb-5 rounded-[20px] border border-red-200 bg-red-50 px-6 py-4 text-base font-semibold text-red-700"
        >
          {{ errorMessage }}
        </div>

        <TicketsStep
          v-if="step === 'tickets'"
          :dates="scrollDates"
          :calendar-dates="dates"
          :selected-date-id="selectedDateId"
          :tickets="tickets"
          :quantities="quantities"
          :lines="selectedTicketLines"
          :total-price="totalPrice"
          :format-price="formatPrice"
          :is-tickets-loading="isTicketsLoading"
          @select-date="selectedDateId = $event"
          @quantity-change="setQuantity"
          @checkout="goToCheckout"
        />

        <CheckoutStep
          v-if="step === 'checkout'"
          ref="checkoutStepRef"
          :form="form"
          :payment-methods="paymentMethods"
          :selected-payment-id="selectedPaymentId"
          :lines="selectedTicketLines"
          :total-price="checkoutTotalPrice"
          :discount-amount="promoDiscountAmount"
          :format-price="formatPrice"
          :is-submitting="isSubmitting"
          :validate-promo-code="validatePromoCode"
          @select-payment="selectedPaymentId = $event"
          @promo-change="setAppliedPromo"
          @submit="submitOrder"
        />

        <OrderNotFoundState v-if="step === 'success' && isOrderNotFound" />

        <SuccessStep
          v-if="step === 'success' && !isOrderNotFound"
          :tickets="successTickets"
          :order-id="displayedOrderNumber"
          :total-quantity="successTotalQuantity"
          :total-price="successTotalPrice"
          :email="successEmail"
          :format-price="formatPrice"
          @return-ticket="returnTicket"
        />
      </template>
    </div>

    <MobileOrderSummary
      v-if="!isLoading && step !== 'success' && selectedTicketLines.length"
      :key="step"
      :lines="selectedTicketLines"
      :total-price="mobileOrderTotalPrice"
      :discount-amount="mobileOrderDiscountAmount"
      :total-quantity="totalQuantity"
      :format-price="formatPrice"
      :action-label="mobileOrderActionLabel"
      loading-label="Оплачиваем..."
      :is-loading="step === 'checkout' && isSubmitting"
      :is-disabled="!selectedTicketLines.length"
      @action="handleMobileOrderAction"
    />
  </main>
</template>
